<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type TestData = App.Students.Data.TestData;

import type { HybridlyFormData } from '@/@types/global';
import type { Selectable } from '@/components/selectBox/selectBox';

import { useDataRequestIndicators } from '@/hooks/useDataRequestIndicators';

import Alert from '@/components/alert/Alert.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormSwitchInput from '@/components/formSwitchInput/FormSwitchInput.vue';
import FormTextareaInput from '@/components/formTextareaInput/FormTextareaInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

import TimesCircle from '@/icons/line/times-circle.svg';
import Fieldset from '@/components/fieldset/Fieldset.vue';

const props = defineProps<{
  dataRequest?: DataRequestData;
  form: HybridlyFormData<{
    test: {
      type: EnumData;
      subject: EnumData;
      test: EnumData;
      date: string;
      scores: Record<string, number>;
    };
    user_comment: string;
  }>;
  apCourses: EnumData[];
  customTests: App.Configuration.Data.CustomTestData[];
  labelMap: Record<string, string>;
  scoreMap: Record<string, Record<string, { rules?: string[]; step?: number }>>;
  test: TestData;
  types: EnumData[];
}>();

const { computedStatusBadge, hasAttributeChange } = useDataRequestIndicators({
  dataRequest: props.dataRequest,
  base: props.test
});

const currentCustomTest = computed(
  () => props.customTests.filter((test) => test.name === props.form.fields.test.test?.value)[0]
);

const shouldShowScores = computed(() => {
  const isComplete = props.form.fields.test.is_complete;
  const isNotCustomTest =
    props.form.fields.test.type && props.form.fields.test.type?.value !== 'custom-test';
  const isCustomTestWithValue =
    props.form.fields.test.type?.value === 'custom-test' && props.form.fields.test.test?.value;

  return isComplete && (isNotCustomTest || isCustomTestWithValue);
});

const isScoresSwitchDisabled = computed(() => {
  const hasNoTypeAndNotCustomTest =
    !props.form.fields.test.type && props.form.fields.test.type?.value !== 'custom-test';
  const isCustomTestWithoutValue =
    props.form.fields.test.type?.value === 'custom-test' && !props.form.fields.test.test?.value;
  const isApCourseWithoutSubject =
    props.form.fields.test.type?.value === 'ap-course' && !props.form.fields.test.subject?.value;

  return hasNoTypeAndNotCustomTest || isCustomTestWithoutValue || isApCourseWithoutSubject;
});

const customTestOptions = props.customTests.map((test) =>
  buildSelectableCustomTest(test)
) as Selectable<string>[];

function hasScoreAttributeChange(scoreName: string): boolean {
  const attributes = props.dataRequest?.attributes as
    | { scores?: { name: string; score: number | string | null }[] }
    | undefined;
  const attrScores = attributes?.scores;
  if (!Array.isArray(attrScores)) {
    return false;
  }
  const attrVal = attrScores.find((s) => s.name === scoreName)?.score;
  if (attrVal === undefined || attrVal === null || attrVal === '') {
    return false;
  }
  const baseVal = (props.test?.scores || []).find((s) => s.name === scoreName)?.score;
  return attrVal !== baseVal;
}

function handleResetScores() {
  props.form.fields.test.scores = {};
}

function handleResetCustomTestScores() {
  ['score', 'score2', 'score3', 'score4', 'score5'].forEach((score) => {
    props.form.fields.test[score] = null;
  });
}

function handleTestTypeChange(type) {
  if (props.form.initial.type?.value === type.value) {
    props.form.reset();
  } else {
    props.form.fields.test.type = type;
    props.form.fields.test.test = null;
    props.form.fields.test.is_complete = false;
    props.form.errors = {};
    handleResetScores();
    handleResetCustomTestScores();
  }
}

function handleSubjectChange(subject) {
  props.form.fields.test.scores.subject = subject.value;
  props.form.fields.test.subject = subject;
}

function handleIsCompleteChange(isComplete) {
  if (props.form.initial.is_complete?.value === isComplete) {
    props.form.reset();
  } else {
    props.form.fields.test.is_complete = isComplete;
    handleResetScores();
    handleResetCustomTestScores();
    if (props.form.fields.test.subject?.value) {
      props.form.fields.test.scores.subject = props.form.fields.test.subject.value;
    }
  }
}
</script>

<template>
  <div class="-mx-5 -mb-8 -mt-6 flex flex-col divide-y divide-slate-200">
    <div class="px-5 pb-5 pt-6" v-if="dataRequest?.status.value === 'rejected'">
      <Alert
        v-if="dataRequest?.status.value === 'rejected'"
        :description="`Reason: ${dataRequest.rejected_reason ?? 'No reason provided'}`"
        :icon="TimesCircle"
        title="Submission was not approved"
        color="danger"
        variant="soft"
      />
    </div>
    <FormRows class="px-5 pb-8 pt-6">
      <FormListboxInput
        label="Exam type"
        :error="form.errors.type"
        :modelValue="form.fields.test.type"
        :options="types"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('type') ? computedStatusBadge.label : undefined"
        @update:modelValue="(value) => handleTestTypeChange(value)"
      />
      <FormListboxInput
        v-if="form.fields.test.type?.value === 'ap-course'"
        label="Course"
        :error="form.errors.scores?.subject"
        :modelValue="form.fields.test.subject"
        :options="apCourses"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasScoreAttributeChange('subject') ? computedStatusBadge.label : undefined
        "
        @update:modelValue="(value) => handleSubjectChange(value)"
      />
      <FormListboxInput
        v-if="form.fields.test.type?.value === 'custom-test'"
        label="Test"
        :error="form.errors.test"
        :modelValue="form.fields.test.test"
        :options="customTestOptions"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('type') ? computedStatusBadge.label : undefined"
        @update:modelValue="(value) => (form.fields.test.test = value)"
      />
      <FormDateInput
        :clearable="false"
        :error="form.errors.date"
        :modelValue="form.fields.test.date"
        format="MMM dd, yyyy"
        label="Date"
        name="date"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('date') ? computedStatusBadge.label : undefined"
        @update:modelValue="(value) => (form.fields.test.date = value)"
      />
      <FormSwitchInput
        :checked="form.fields.test.is_complete"
        :error="form.errors.is_complete"
        :isDisabled="isScoresSwitchDisabled"
        label="Add Exam Scores?"
        name="is_complete"
        helperText="If the exam is complete, you can add the scores."
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('is_complete') ? computedStatusBadge.label : undefined
        "
        @update:checked="(value) => handleIsCompleteChange(value)"
      />
    </FormRows>
    <Fieldset v-if="shouldShowScores" title="Scores" class="p-6 pb-8">
      <template v-if="form.fields.test.type?.value !== 'custom-test'">
        <FormTextInput
          v-for="(values, score) in scoreMap[form.fields.test.type?.value]"
          :key="score"
          :error="form.errors?.test?.scores?.[score]"
          :label="labelMap[score]"
          :modelValue="form.fields.test.scores[score]"
          :step="values?.step"
          :name="score"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange(score as string) ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.scores[score] = value)"
        />
      </template>
      <template v-if="form.fields.test.type?.value === 'custom-test'">
        <FormTextInput
          v-if="currentCustomTest?.score"
          :error="form.errors?.test?.score"
          :label="currentCustomTest.score"
          :modelValue="form.fields.test.score"
          name="score"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange('score') ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.score = value)"
        />
        <FormTextInput
          v-if="currentCustomTest?.score2"
          :error="form.errors?.test?.score2"
          :label="currentCustomTest.score2"
          :modelValue="form.fields.test.score2"
          name="score2"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange('score2') ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.score2 = value)"
        />
        <FormTextInput
          v-if="currentCustomTest?.score3"
          :error="form.errors?.test?.score3"
          :label="currentCustomTest.score3"
          :modelValue="form.fields.test.score3"
          name="score3"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange('score3') ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.score3 = value)"
        />
        <FormTextInput
          v-if="currentCustomTest?.score4"
          :error="form.errors?.test?.score4"
          :label="currentCustomTest.score4"
          :modelValue="form.fields.test.score4"
          name="score4"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange('score4') ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.score4 = value)"
        />
        <FormTextInput
          v-if="currentCustomTest?.score5"
          :error="form.errors?.test?.score5"
          :label="currentCustomTest.score5"
          :modelValue="form.fields.test.score5"
          name="score5"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasScoreAttributeChange('score5') ? computedStatusBadge.label : undefined
          "
          type="number"
          @update:modelValue="(value) => (form.fields.test.score5 = value)"
        />
      </template>
    </Fieldset>
    <FormRows class="px-5 pb-8 pt-6">
      <FormTextareaInput
        label="Note"
        helpText="Note to adviser about this submission"
        :error="form.errors.user_comment"
        :modelValue="form.fields.user_comment"
        name="user_comment"
        isOptional
        @update:modelValue="(value) => (form.fields.user_comment = value)"
      />
    </FormRows>
  </div>
</template>
